(() => {
  // The app embed may render reviews first. Keep product labels current in either path.
  const syncProductLabels = () => {
    for (const root of document.querySelectorAll('[data-holy-cow-reviews]:not([data-title-sync])')) {
      root.dataset.titleSync = 'true';
      const sync = () => {
        for (const card of root.querySelectorAll('.holy-cow-review__product[data-product-handle]')) {
          if (card.dataset.productHandle !== root.dataset.productHandle) continue;
          const title = card.querySelector('strong');
          if (title && root.dataset.productName && title.textContent !== root.dataset.productName) {
            title.textContent = root.dataset.productName;
            card.dataset.productTitle = root.dataset.productName;
          }
        }
      };
      sync();
      new MutationObserver(sync).observe(root, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncProductLabels, { once: true });
  else syncProductLabels();
  if (window.__holyCowReviewsLoaded) return;
  window.__holyCowReviewsLoaded = true;

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character]);

  const stars = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return `<span class="holy-cow-review-stars" aria-label="${rounded} out of 5 stars">${'★'.repeat(rounded)}<span class="holy-cow-review-stars--empty">${'★'.repeat(5 - rounded)}</span></span>`;
  };

  async function json(url) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { accept: 'application/json' } });
    if (!response.ok) throw new Error(`Reviews request failed (${response.status})`);
    return response.json();
  }

  for (const root of document.querySelectorAll('[data-holy-cow-review-summary]:not([data-ready])')) {
    root.dataset.ready = 'true';
    json(root.dataset.endpoint).then((data) => {
      const starRoot = root.querySelector('.holy-cow-review-stars');
      starRoot.innerHTML = stars(data.average);
      starRoot.setAttribute('aria-label', `${data.average} out of 5 stars`);
      root.querySelector('[data-holy-cow-review-summary-text]').textContent = data.count
        ? `${Number(data.average).toFixed(1)}/5 (${data.count} ${data.count === 1 ? 'testimonial' : 'testimonials'})`
        : 'No reviews yet';
    }).catch(() => {
      root.querySelector('[data-holy-cow-review-summary-text]').textContent = 'Reviews unavailable';
    });
  }

  for (const root of document.querySelectorAll('[data-holy-cow-reviews]:not([data-ready])')) {
    root.dataset.ready = 'true';
    const endpoint = root.dataset.endpoint;
    const pageSize = Number(root.dataset.pageSize) || 10;
    const list = root.querySelector('[data-holy-cow-reviews-list]');
    const aggregate = root.querySelector('[data-holy-cow-reviews-aggregate]');
    const filters = root.querySelector('[data-holy-cow-review-filters]');
    const more = root.querySelector('[data-holy-cow-reviews-more]');
    const reviewDialog = root.querySelector('[data-holy-cow-review-dialog]');
    const reviewForm = root.querySelector('[data-holy-cow-review-form]');
    const reviewStatus = root.querySelector('[data-holy-cow-review-status]');
    const reviewOpen = root.querySelector('[data-holy-cow-review-open]');
    const scope = root.dataset.scope || 'product';
    let page = 1;
    let rating = 0;
    let total = 0;

    const hydrateProductCards = () => {
      for (const card of list.querySelectorAll('[data-product-handle]:not([data-product-ready])')) {
        card.dataset.productReady = 'true';
        const image = card.querySelector('[data-product-image]');
        fetch(`/products/${encodeURIComponent(card.dataset.productHandle)}.js`, {
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) throw new Error('Product unavailable');
          return response.json();
        }).then((product) => {
          const title = card.querySelector('strong');
          if (product.title && title) {
            title.textContent = product.title;
            card.dataset.productTitle = product.title;
          }
          const imageUrl = product.featured_image || product.images?.[0];
          if (!imageUrl || !image) return;
          image.src = imageUrl;
          image.alt = product.title || card.dataset.productTitle || 'Reviewed Holy Cow product';
          image.hidden = false;
          card.classList.add('has-image');
        }).catch(() => {});
      }
    };

    const renderReview = (review) => {
      const isLong = String(review.body || '').length > 420;
      const experience = review.protocolTitle || review.productTitle;
      const r3vyApproved = review.approvedByR3vy === true;
      const initials = String(review.displayName || 'Holy Cow customer')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase();
      const productMarkup = experience
        ? review.productHandle
          ? `<a id="holy-cow-review-product-${escapeHtml(review.id)}" class="holy-cow-review__product" href="/products/${encodeURIComponent(review.productHandle)}" data-product-handle="${escapeHtml(review.productHandle)}" data-product-title="${escapeHtml(experience)}">
              <span class="holy-cow-review__product-image"><img data-product-image src="" alt="" width="56" height="56" loading="lazy" hidden></span>
              <span><small>Review for</small><strong>${escapeHtml(experience)}</strong></span>
            </a>`
          : `<div class="holy-cow-review__product"><span><small>Review for</small><strong>${escapeHtml(experience)}</strong></span></div>`
        : '';
      return `<article class="holy-cow-review">
      <header>${Number.isInteger(Number(review.rating)) && Number(review.rating) >= 1 && Number(review.rating) <= 5 ? stars(review.rating) : '<span class="holy-cow-review__unrated">Customer experience · Not rated</span>'}</header>
      <div class="holy-cow-review__reviewer">
        <span class="holy-cow-review__avatar" aria-hidden="true">${escapeHtml(initials)}</span>
        <span class="holy-cow-review__identity">
          <span class="holy-cow-review__identity-row"><strong>${escapeHtml(review.displayName)}</strong>${review.verified ? '<span class="holy-cow-review-verified">Verified buyer</span>' : r3vyApproved ? '<span class="holy-cow-review-approved">Reviewed testimonial</span>' : '<span class="holy-cow-review-legacy">Customer testimonial</span>'}${review.incentivized ? '<span class="holy-cow-review-incentive">Incentivized</span>' : ''}</span>
          <time datetime="${escapeHtml(review.publishedAt)}">${new Date(review.publishedAt).toLocaleDateString()}</time>
        </span>
      </div>
      ${review.title ? `<h3>${escapeHtml(review.title)}</h3>` : ''}
      <p class="holy-cow-review__body${isLong ? ' is-collapsed' : ''}">${escapeHtml(review.body)}</p>
      ${isLong ? '<button class="holy-cow-review__toggle" type="button" data-review-toggle aria-expanded="false">Read full testimonial</button>' : ''}
      ${review.videoUrl ? `<video class="holy-cow-review__media" controls preload="metadata"${review.videoPosterUrl ? ` poster="${escapeHtml(review.videoPosterUrl)}"` : ''}><source src="${escapeHtml(review.videoUrl)}">Your browser does not support this customer video.</video>` : ''}
      ${!review.videoUrl && review.photoUrl ? `<img class="holy-cow-review__media" src="${escapeHtml(review.photoUrl)}" alt="Photo shared with this customer testimonial" loading="lazy" width="720" height="720">` : ''}
      ${productMarkup}
    </article>`;
    };

    async function load({ reset = false } = {}) {
      more.disabled = true;
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('v', '20260919-collected-testimonials');
      url.searchParams.set('scope', scope);
      url.searchParams.set('page', String(page));
      url.searchParams.set('limit', String(pageSize));
      if (rating) url.searchParams.set('rating', String(rating));
      const data = await json(url);
      total = data.total;
      if (reset) list.innerHTML = '';
      list.insertAdjacentHTML('beforeend', data.reviews.map(renderReview).join(''));
      hydrateProductCards();
      if (window.location.hash.startsWith('#holy-cow-review-product-')) {
        requestAnimationFrame(() => {
          document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: 'center' });
        });
      }
      aggregate.classList.toggle('is-empty', data.aggregate.count === 0);
      aggregate.innerHTML = data.aggregate.count
        ? `<strong>${Number(data.aggregate.average).toFixed(1)}</strong>${stars(data.aggregate.average)}<span>${data.aggregate.count} customer ${data.aggregate.count === 1 ? 'testimonial' : 'testimonials'}</span>`
        : '<strong>No testimonials yet</strong><span>Be the first verified customer to share an experience.</span>';
      filters.hidden = data.aggregate.count === 0;
      for (const item of filters.querySelectorAll('[data-rating]')) {
        const value = Number(item.dataset.rating);
        item.hidden = value > 0 && !Number(data.aggregate.distribution?.[value] || 0);
      }
      more.hidden = page * pageSize >= total;
      more.disabled = false;

      const existingSchema = root.querySelector('script[data-holy-cow-review-schema]');
      if (existingSchema) existingSchema.remove();
      if (data.aggregate.count && scope !== 'brand') {
        const schema = document.createElement('script');
        schema.type = 'application/ld+json';
        schema.dataset.holyCowReviewSchema = 'true';
        schema.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: root.dataset.productName,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: data.aggregate.average,
            reviewCount: data.aggregate.ratedCount || data.aggregate.count,
            bestRating: 5,
            worstRating: 1,
          },
        });
        root.append(schema);
      }
    }

    filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rating]');
      if (!button) return;
      rating = Number(button.dataset.rating) || 0;
      page = 1;
      for (const item of filters.querySelectorAll('[data-rating]')) {
        item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
      }
      load({ reset: true }).catch(() => {
        aggregate.textContent = 'Reviews are temporarily unavailable.';
      });
    });
    more.addEventListener('click', () => {
      page += 1;
      load().catch(() => {
        page -= 1;
        more.disabled = false;
      });
    });
    let targetsLoaded = false;
    const loadTargets = async () => {
      if (targetsLoaded || !reviewForm) return;
      const data = await json('/apps/holy-cow-reviews/targets?v=2');
      const currentProductId = String(root.dataset.productId || '').replace(/\D/g, '');
      const target = data.targets.find((item) => String(item.productId || '').endsWith(`/${currentProductId}`));
      if (!target) throw new Error('Review product is unavailable.');
      reviewForm.elements.targetKey.value = target.key;
      targetsLoaded = true;
    };
    reviewOpen?.addEventListener('click', async () => {
      reviewStatus.textContent = '';
      reviewDialog.showModal();
      try {
        await loadTargets();
      } catch {
        reviewStatus.textContent = 'The review form is temporarily unavailable. Please try again.';
      }
    });
    reviewDialog?.querySelector('[data-holy-cow-review-close]')?.addEventListener('click', () => reviewDialog.close());
    reviewDialog?.addEventListener('click', (event) => {
      if (event.target === reviewDialog) reviewDialog.close();
    });
    reviewForm?.elements.body?.addEventListener('input', (event) => {
      const count = event.currentTarget.value.length;
      reviewForm.querySelector('[data-review-character-count]').textContent = `${count} / 75 minimum`;
    });
    reviewForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = reviewForm.querySelector('[type="submit"]');
      const formData = new FormData(reviewForm);
      const payload = {
        targetKey: formData.get('targetKey'),
        rating: Number(formData.get('rating')),
        displayName: '',
        email: formData.get('email'),
        title: '',
        body: formData.get('body'),
        consentDisplayName: false,
        honestReviewConsent: true,
        consentUgc: false,
      };
      submit.disabled = true;
      reviewStatus.classList.remove('is-error', 'is-success');
      reviewStatus.textContent = 'Submitting your review…';
      try {
        const response = await fetch('/apps/holy-cow-reviews/submit', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { accept: 'application/json', 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Unable to submit review.');
        reviewStatus.classList.add('is-success');
        reviewStatus.textContent = result.message;
        reviewForm.reset();
        reviewForm.querySelector('[data-review-character-count]').textContent = '0 / 75 minimum';
        submit.textContent = 'Submitted for approval';
      } catch (error) {
        reviewStatus.classList.add('is-error');
        reviewStatus.textContent = error.message || 'Unable to submit review. Please try again.';
        submit.disabled = false;
      }
    });
    list.addEventListener('click', (event) => {
      const button = event.target.closest('[data-review-toggle]');
      if (!button) return;
      const body = button.previousElementSibling;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      body.classList.toggle('is-collapsed', isExpanded);
      button.setAttribute('aria-expanded', String(!isExpanded));
      button.textContent = isExpanded ? 'Read full testimonial' : 'Show less';
    });
    load({ reset: true }).catch(() => {
      aggregate.textContent = 'Reviews are temporarily unavailable.';
    });
  }
})();
